import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../data/config';
import SEO, { pageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ChevronLeft, FileText } from 'lucide-react';

const TermsPage = () => {
  const { language, getRoute } = useLanguage();

  const content = {
    fr: {
      title: 'Conditions d\'utilisation',
      lastUpdated: 'Dernière mise à jour: Janvier 2026',
      intro: `En utilisant les services de ${siteConfig.businessName}, vous acceptez les conditions suivantes.`,
      sections: [
        {
          title: 'Services',
          content: `${siteConfig.businessName} offre des services de transport par taxi dans la région du Grand Montréal. Nous nous efforçons de fournir un service ponctuel et professionnel.`
        },
        {
          title: 'Réservations',
          content: `Les réservations peuvent être effectuées par téléphone, via notre application mobile ou en ligne. Les réservations sont sujettes à la disponibilité des véhicules.`
        },
        {
          title: 'Tarifs',
          content: `Les tarifs sont réglementés par la Commission des transports du Québec. Le tarif final dépend de la distance parcourue et du temps de trajet. Des frais supplémentaires peuvent s'appliquer pour certains services (bagages volumineux, attente prolongée).`
        },
        {
          title: 'Paiement',
          content: `Nous acceptons les paiements en espèces et par carte (Visa, Mastercard, Amex, débit). Le paiement est dû à la fin de la course.`
        },
        {
          title: 'Annulations',
          content: `Vous pouvez annuler une réservation en nous contactant. Des frais peuvent s'appliquer pour les annulations de dernière minute ou les absences.`
        },
        {
          title: 'Responsabilité',
          content: `${siteConfig.businessName} n'est pas responsable des retards causés par des circonstances hors de notre contrôle (trafic, météo, etc.). La responsabilité pour les objets perdus est limitée.`
        },
      ],
    },
    en: {
      title: 'Terms of Use',
      lastUpdated: 'Last updated: January 2026',
      intro: `By using ${siteConfig.businessName} services, you agree to the following terms.`,
      sections: [
        {
          title: 'Services',
          content: `${siteConfig.businessName} offers taxi transportation services in the Greater Montreal area. We strive to provide punctual and professional service.`
        },
        {
          title: 'Reservations',
          content: `Reservations can be made by phone, through our mobile app, or online. Reservations are subject to vehicle availability.`
        },
        {
          title: 'Fares',
          content: `Fares are regulated by the Commission des transports du Québec. The final fare depends on distance traveled and trip duration. Additional fees may apply for certain services (oversized luggage, extended waiting).`
        },
        {
          title: 'Payment',
          content: `We accept cash and card payments (Visa, Mastercard, Amex, debit). Payment is due at the end of the ride.`
        },
        {
          title: 'Cancellations',
          content: `You may cancel a reservation by contacting us. Fees may apply for last-minute cancellations or no-shows.`
        },
        {
          title: 'Liability',
          content: `${siteConfig.businessName} is not responsible for delays caused by circumstances beyond our control (traffic, weather, etc.). Liability for lost items is limited.`
        },
      ],
    },
  };

  const c = content[language];

  return (
    <div className="min-h-screen pt-20 pb-20" data-testid="terms-page">
      <SEO {...pageSEO.terms} />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Button asChild variant="ghost" className="mb-8">
          <Link to={getRoute('/')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Retour' : 'Back'}
          </Link>
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">{c.title}</h1>
            <p className="text-muted-foreground text-sm">{c.lastUpdated}</p>
          </div>
        </div>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            <p className="text-muted-foreground mb-8">{c.intro}</p>
            
            <div className="space-y-8">
              {c.sections.map((section, index) => (
                <div key={index}>
                  <h2 className="font-heading font-bold text-xl text-foreground mb-3">
                    {section.title}
                  </h2>
                  <p className="text-muted-foreground">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsPage;
